// Record money v2 — the pieces every Record form shares (UI rebuild batch 3, Astra's record / expense / capital / drawing
// mockups): the two-column page (the form card, and at the right from lg the balance and the Last 10), the form's foot
// (why the button is off, when it is, then the one gold primary), the readback a Review opens before the post, and the
// "About this job" fold. Layout only — every guard lives in the form that uses them.
import { useState, type ReactNode } from 'react';
import { ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { Fact } from '../kit';

/** The form card and, from lg, its side column (Astra: "a secondary column only for the current balance or effect"). */
export function FormPage({ form, aside, bare = false }: {
  form: ReactNode;
  aside?: ReactNode;
  /** A form that draws its own card (another builder's): the column without this card around it. */
  bare?: boolean;
}) {
  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:items-start xl:gap-6">
      {bare ? <div className="min-w-0">{form}</div>
        : <section className="min-w-0 space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:p-6">{form}</section>}
      {aside && <aside className="hidden min-w-0 space-y-4 xl:block">{aside}</aside>}
    </div>
  );
}

/** A side column's head: a title over its body. */
export function AsideBlock({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div>
      <h2 className={`${sub ? '' : 'mb-2 '}text-title font-semibold`}>{title}</h2>
      {sub && <p className="mb-2 break-words text-meta text-tg-hint">{sub}</p>}
      {children}
    </div>
  );
}

/** "About this job" — the screen's explanation and its context (the desk, when the books were read), behind a tap. The
 *  toggle is its own full-width 44 px row, so on the phone it never shares a touch area with the line above (Astra,
 *  1 Nov 2026). */
export function AboutFold({ help, facts, className = '' }: {
  help: ReactNode;
  facts?: ReadonlyArray<{ label: string; value: ReactNode }>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border-t border-stroke ${className}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
        className="btn-link flex min-h-11 w-full items-center gap-1 text-left text-meta">
        {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}About this job
      </button>
      {open && (
        <div className="space-y-2 pb-2 text-meta text-tg-hint">
          {help}
          {facts && facts.length > 0 && <AsideFacts items={facts} />}
        </div>
      )}
    </div>
  );
}

/** The side column's facts (Astra: Desk, Read). */
export function AsideFacts({ items }: { items: ReadonlyArray<{ label: string; value: ReactNode }> }) {
  return <div>{items.map((it) => <Fact key={it.label} label={it.label}>{it.value}</Fact>)}</div>;
}

/** The "Review …" button: the gold primary until a review is open. Disabled by exactly the rule the post itself has. */
export function ReviewButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button type="button" className="btn-primary w-full text-body lg:w-auto lg:px-6" disabled={disabled} onClick={onClick}>
      {label}<ArrowRight className="h-4 w-4" aria-hidden />
    </button>
  );
}

/** The form's foot: a hairline, the one muted line when there is something to say (why the button is off) and the
 *  button. The button sits first on the phone (full width, the line under it), at the right from lg. No standing "review
 *  once" sentence: the Review button says it (Astra, 1 Nov 2026). */
export function FormFoot({ line, children }: { line?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 lg:flex-row lg:items-center lg:justify-between lg:pt-5">
      {line ? <p className="text-center text-meta text-tg-hint lg:text-left">{line}</p> : <span className="max-lg:hidden" />}
      <div className="lg:shrink-0">{children}</div>
    </div>
  );
}

/** THE READBACK BEFORE THE POST (Astra: "show a complete readback before the single irreversible confirmation"): what
 *  will post, read from the live fields; Edit closes it, the confirm is the form's own post button. */
export function ReviewPanel({ rows, effect, onEdit, confirm, notices, why }: {
  rows: ReadonlyArray<{ label: string; value: ReactNode } | null | false>;
  effect?: ReactNode;
  onEdit: () => void;
  confirm: ReactNode;
  /** A prerequisite read that failed while reviewing (the fields that carried it are put away on Review): its notice and
   *  its Try again, never hidden by Review (Astra, 1 Nov 2026). */
  notices?: ReactNode;
  /** Why Confirm is off — said beside it, never a dead button. */
  why?: ReactNode;
}) {
  return (
    <div className="space-y-3 border-t border-stroke pt-4 first:border-t-0 first:pt-0 lg:pt-5 lg:first:pt-0">
      <h2 className="text-body font-semibold">Review</h2>
      {notices}
      <div>
        {rows.filter((r): r is { label: string; value: ReactNode } => !!r).map((r) => <Fact key={r.label} label={r.label}>{r.value}</Fact>)}
      </div>
      {effect && <p className="text-meta text-tg-hint">{effect}</p>}
      <div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-end">
        {why && <p className="text-center text-meta text-tg-hint lg:mr-auto lg:text-left">{why}</p>}
        <button type="button" className="btn-secondary w-full lg:w-auto lg:px-6" onClick={onEdit}>Edit</button>
        {confirm}
      </div>
    </div>
  );
}
